import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import apiService from "../../services/api";
import { filterBySearchTerm } from "../../utils/search";
import { usePopup } from "../common/Popup";
import trashIcon from "../../assets/trash.png";
import editIcon from "../../assets/edit.png";
import moreIcon from "../../assets/more.png";
import visibilityIcon from "../../assets/visibility.png";

const Outer = styled.div`
  display: flex;
  justify-content: center;
  font-family: public-sans, sans-serif;
`;

const Container = styled.div`
  width: 100%;
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1.15fr 0.7fr 1.0fr 0.65fr 0.7fr 0.85fr 170px;
  align-items: center;
  padding-left: 20px;
  height: 50px;
  background: #f2f0f0;
  font-size: 16px;
  font-weight: 700;
  color: #6ab8a8;
  gap: 0;

  > div {
    padding: 0 8px;
    border-right: 1px solid #d0d0d0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    text-align: center;
    height: 100%;

    &:last-child {
      border-right: none;
    }
  }
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const UnitTableWrapper = styled.div`
  border-right: 1px solid #dfe3e8;
  border-bottom: 1px solid #e6e6e6;
  margin-bottom: 18px;
`;

const UnitTitle = styled.div`
  padding: 14px 32px;
  background: #f2f0f0;
  font-size: 16px;
  font-weight: 400;
  color: #6ab8a8;
  font-weight: 600;
`;

const UnitTitleValue = styled.span`
  color: #555555;
  padding-left: 8px;
`;

const UnitSeparator = styled.div`
  height: 1px;
  background: #dfe3e8;
`;

const UnitRowsWrapper = styled.div``;

const ListRow = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1.15fr 0.7fr 1.0fr 0.65fr 0.7fr 0.85fr 170px;
  align-items: center;
  padding-left: 20px;
  height: 50px;
  gap: 0;
  font-size: 16px;
  color: #555555;
  font-weight: 500;
  border-top: 1px solid #e6e6e6;

  &:nth-child(odd) {
    background: #e8f5f3;
  }

  &:nth-child(even) {
    background: #ffffff;
  }

  &:hover {
    background: #f9fefa;
  }

  > div {
    padding: 0 8px;
    border-right: 1px solid #d0d0d0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;

    &:last-child {
      border-right: none;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
`;

const ActionButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;

  img {
    width: 22px;
    height: 22px;
  }
`;

const ActionLabel = styled.div`
  font-size: 14px;
  color: #ff9f1f;
`;

const SolicitadoLabel = styled.div`
  font-size: 14px;
  color: #6ab8a8;
`;

const CheckIcon = styled.div`
  font-size: 24px;
  line-height: 24px;
  color:rgb(0, 0, 0);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SolicitationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  width: 100%;
  column-gap: 10px;
  row-gap: 4px;
`;

const SolicitationCount = styled.div`
  color: #ff9f1f;
  font-size: 16px;
  font-weight: 800;
  justify-self: end; /* deixa o número encostado no ícone */
  align-self: center;
  grid-column: 1;
  grid-row: 1 / 3;
`;

const SolicitationVerLabel = styled(ActionLabel)`
  grid-column: 2;
  grid-row: 2;
  justify-self: center;
  text-align: center;
`;

const EmptyMessage = styled.div`
  padding: 32px;
  text-align: center;
  color: #555;
  font-size: 15px;
`;

const formatTipoAtividade = (tipo) => {
  const tipos = {
    'ESTAGIO_OBRIGATORIO': 'Estágio Obrigatório',
    'ESTAGIO_SUPERVISIONADO': 'Estágio Supervisionado',
    'INTERNATO_MEDICO': 'Internato Médico',
    'AULA_PRATICA': 'Aula Prática'
  };
  return tipos[tipo] || tipo;
};

const formatValueOrDash = (value) => {
  if (value === null || value === undefined) return "-";
  return value;
};

const getTipoFilterValue = (tipoFilter) => {
  const tipoMap = {
    'estagio-obrigatorio': 'ESTAGIO_OBRIGATORIO',
    'residencia': 'INTERNATO_MEDICO',
    'aula-pratica': 'AULA_PRATICA'
  };
  return tipoMap[tipoFilter] || null;
};

const VagasContent = ({ user, sortOrder, searchTerm = '', tipoFilter = null, onVerVaga, onEditarVaga }) => {
  const popup = usePopup();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [solicitacoesCountByVagaId, setSolicitacoesCountByVagaId] = useState({});
  const [solicitadasVagaIds, setSolicitadasVagaIds] = useState(new Set());

  const navigate = useNavigate();
  const isSolicitante = user?.cargoId === 2 || user?.cargoId === 3;

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};

      if (user?.cargoId === 4 && user?.estabelecimentoSaudeId) {
        params.estabelecimentoSaudeId = user.estabelecimentoSaudeId;
      }

      const result = await apiService.getVagas(params);
      const items = result.data ? result.data : result;
      setItems(items);
    } catch (err) {
      setError(err.message || "Falha ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchItems();
    }
  }, [user]);

  useEffect(() => {
    const fetchSolicitacoesCount = async () => {
      if (!user || isSolicitante) return;
      try {
        const result = await apiService.getSolicitacoesVaga();
        const data = result.data ? result.data : result;

        const map = data.reduce((acc, solicitacao) => {
          const id = solicitacao.vaga?.id ?? solicitacao.vagaId;
          if (id === null || id === undefined) return acc;
          const numericId = Number(id);
          if (Number.isNaN(numericId)) return acc;
          acc[numericId] = (acc[numericId] || 0) + 1;
          return acc;
        }, {});

        setSolicitacoesCountByVagaId(map);
      } catch (err) {
        console.error("Falha ao carregar contagem de solicitações:", err);
        setSolicitacoesCountByVagaId({});
      }
    };

    fetchSolicitacoesCount();
  }, [user, isSolicitante]);

  useEffect(() => {
    const fetchSolicitadasIds = async () => {
      if (!user || !isSolicitante) return;
      try {
        const result = await apiService.getSolicitacoesVaga();
        const data = result.data ? result.data : result;

        const ids = new Set(
          data
            .map((s) => {
              const id = s.vaga?.id ?? s.vagaId;
              return id !== null && id !== undefined ? Number(id) : null;
            })
            .filter((id) => id !== null && !Number.isNaN(id))
        );

        setSolicitadasVagaIds(ids);
      } catch (err) {
        console.error("Falha ao carregar solicitações do usuário:", err);
        setSolicitadasVagaIds(new Set());
      }
    };

    fetchSolicitadasIds();
  }, [user, isSolicitante]);

  const tipoFilterValue = tipoFilter ? getTipoFilterValue(tipoFilter) : null;

  const itemsFilteredByTipo = tipoFilterValue
    ? items.filter((vaga) => vaga.tipoAtividade === tipoFilterValue)
    : items;

  const filteredItems = searchTerm
    ? filterBySearchTerm(itemsFilteredByTipo, searchTerm, [
        (vaga) => vaga.estabelecimentoSaude?.nome || '',
        (vaga) => vaga.unidadeAssistencial?.especialidade?.nome || '',
        (vaga) => vaga.unidadeAssistencial?.nome || '',
        (vaga) => formatTipoAtividade(vaga.tipoAtividade) || ''
      ])
    : itemsFilteredByTipo;

  const sortedItems = sortOrder
    ? [...filteredItems].sort((a, b) => {
        const valueA = (a.estabelecimentoSaude?.nome || '').toString().toLowerCase();
        const valueB = (b.estabelecimentoSaude?.nome || '').toString().toLowerCase();

        if (sortOrder === 'desc') {
          return valueB.localeCompare(valueA, 'pt-BR');
        }

        return valueA.localeCompare(valueB, 'pt-BR');
      })
    : filteredItems;

  const deleteOne = async (id) => {
    const confirmed = await popup.confirm({
      icon: "delete",
      message: "Confirma exclusão desta vaga?",
    });
    if (!confirmed) return;
    try {
      await apiService.deleteVaga(id);
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (err) {
      await popup.alert({
        icon: "warning",
        title: "Erro ao deletar",
        message: err.message || "Tente novamente.",
      });
    }
  };

  const isSolicitarDisabled = (vaga) => {
    const qtd = vaga?.quantidadeDisponivel;
    return qtd === null || qtd === undefined || Number(qtd) <= 0;
  };

  const handleSolicitar = (vaga) => {
    navigate("/solicitarVaga", { state: { vaga } });
  };

  const handleVerSolicitacoes = (vaga) => {
    navigate("/Concessoes", { state: { vagaId: vaga.id } });
  };

  const groupedByUnidade = sortedItems.reduce((acc, vaga) => {
    const unidadeNome = vaga.estabelecimentoSaude?.nome || "-";
    if (!acc[unidadeNome]) acc[unidadeNome] = [];
    acc[unidadeNome].push(vaga);
    return acc;
  }, {});

  const unidadeNames = Object.keys(groupedByUnidade);

  return (
    <Outer>
      <Container>
        {loading && <EmptyMessage>Carregando...</EmptyMessage>}
        {error && <EmptyMessage>{error}</EmptyMessage>}
        {!loading && sortedItems.length === 0 && (
          <EmptyMessage>Nenhuma vaga disponibilizada.</EmptyMessage>
        )}

        {!loading && sortedItems.length > 0 && (
          <ListWrapper>
            {unidadeNames.map((unidadeNome) => (
              <UnitTableWrapper key={unidadeNome}>
                <UnitTitle>
                  Unidade de saúde: <UnitTitleValue>{unidadeNome}</UnitTitleValue>
                </UnitTitle>
                <UnitSeparator />
                <HeaderRow>
                  <div>ESPECIALIDADE</div>
                  <div>SETOR</div>
                  <div>TURNO</div>
                  <div>TIPO</div>
                  <div>VAGAS OFERTADAS</div>
                  <div>VAGAS DISPONÍVEIS</div>
                  <div>{isSolicitante ? "SOLICITAR" : "SOLICITAÇÕES"}</div>
                  <div>{isSolicitante ? "VER" : "Editar/ Excluir"}</div>
                </HeaderRow>

                <UnitRowsWrapper>
                  {groupedByUnidade[unidadeNome].map((vaga) => (
                    <ListRow key={vaga.id}>
                      <div>{vaga.unidadeAssistencial?.especialidade?.nome || '-'}</div>
                      <div>{vaga.unidadeAssistencial?.nome || '-'}</div>
                      <div>{vaga.turno || '-'}</div>
                      <div>{formatTipoAtividade(vaga.tipoAtividade)}</div>
                      <div>{formatValueOrDash(vaga.quantidade)}</div>
                      <div>{formatValueOrDash(vaga.quantidadeDisponivel)}</div>

                      <div>
                        {isSolicitante ? (
                          solicitadasVagaIds.has(Number(vaga.id)) ? (
                            <ActionButton
                              title="Você já solicitou esta vaga"
                              style={{ cursor: "default", opacity: 0.8 }}
                            >
                              <CheckIcon>&#10003;</CheckIcon>
                              <SolicitadoLabel>Solicitado</SolicitadoLabel>
                            </ActionButton>
                          ) : (
                            <ActionButton
                              onClick={() => {
                                if (isSolicitarDisabled(vaga)) return;
                                handleSolicitar(vaga);
                              }}
                              title={isSolicitarDisabled(vaga) ? "Sem vagas disponíveis" : "Solicitar vaga"}
                              style={
                                isSolicitarDisabled(vaga)
                                  ? { cursor: "not-allowed", opacity: 0.55 }
                                  : undefined
                              }
                            >
                              <img src={moreIcon} alt="Solicitar" />
                              <ActionLabel>Solicitar</ActionLabel>
                            </ActionButton>
                          )
                        ) : (
                          (() => {
                            const count = solicitacoesCountByVagaId[Number(vaga.id)] || 0;
                            if (count <= 0) {
                              return <div style={{ textAlign: "center" }}>---</div>;
                            }

                            return (
                              <ActionButton
                                onClick={() => handleVerSolicitacoes(vaga)}
                                title="Ver solicitações"
                              >
                                <SolicitationGrid>
                                  <SolicitationCount>{count}</SolicitationCount>
                                  <img
                                    src={visibilityIcon}
                                    alt="Olho"
                                    style={{
                                      justifySelf: "center",
                                      gridColumn: 2,
                                      gridRow: 1,
                                      display: "block"
                                    }}
                                  />
                                  <SolicitationVerLabel>ver</SolicitationVerLabel>
                                </SolicitationGrid>
                              </ActionButton>
                            );
                          })()
                        )}
                      </div>

                      <Actions>
                        {isSolicitante ? (
                          <ActionButton
                            onClick={() => onVerVaga && onVerVaga(vaga)}
                            title="Ver detalhes da vaga"
                          >
                            <img src={visibilityIcon} alt="Ver" />
                            <ActionLabel>Ver</ActionLabel>
                          </ActionButton>
                        ) : (
                          <>
                            <ActionButton
                              onClick={() => onEditarVaga && onEditarVaga(vaga)}
                              title="Editar vaga"
                            >
                              <img src={editIcon} alt="Editar" />
                              <ActionLabel>Editar</ActionLabel>
                            </ActionButton>
                            <ActionButton
                              onClick={() => deleteOne(vaga.id)}
                              title="Deletar vaga"
                            >
                              <img src={trashIcon} alt="Deletar" />
                              <ActionLabel>Excluir</ActionLabel>
                            </ActionButton>
                          </>
                        )}
                      </Actions>
                    </ListRow>
                  ))}
                </UnitRowsWrapper>
              </UnitTableWrapper>
            ))}
          </ListWrapper>
        )}
      </Container>
    </Outer>
  );
};

export default VagasContent;
